'use client';

import { INTERVAL_TIME } from '@workspace/constants';
import type { SanityImage } from '@workspace/interfaces';
import { cn } from '@workspace/lib';
import { HeroBannerControls, HeroBannerSlide } from './ui';
import { useMemo } from 'react';
import { getHeroBannerMinHeightClass, getHeroBannerSize } from './hero-banner-util';
import { HeroBannerSize } from './hero-banner.types';
import { useHeroBannerSlide } from './useHeroBannerSlide';

export interface HeroBannerCarouselProps {
  className?: string;
  block: {
    isActive?: boolean;
    heroBannerTypeStyle?: HeroBannerSize;
    slides: Array<{
      _key: string;
      _type: string;
      tag?: string;
      isVideo: boolean;
      videoUrl?: string;
      heading: {
        _type: string;
        fontSize?: 'h1' | 'h2' | 'h3';
        headingText: string;
      };
      description?: string;
      contentPosition?: 'left' | 'center' | 'right';
      textColor?: string;
      image?: SanityImage;
      buttons?: Array<{
        label: string | null;
        style: 'primary' | 'secondary' | 'tertiary' | 'text';
        anchor?: string | null;
        isOpenInNewTab?: boolean | null;
        linkType?: 'internal' | 'external' | 'anchor' | 'askGreg';
        url?: string | null;
        link?: {
          type?: string;
          url?: string;
          anchor?: string;
          isOpenInNewTab?: boolean;
        };
      }> | null;
      socialLinks?: Array<{
        platform: string;
        url?: string;
        isOpenInNewTab?: boolean;
        iconUrl?: string;
      } | null>;
      isActive?: boolean;
    }>;
  };
  autoPlay?: boolean;
  interval?: number;
}

export function HeroBannerCarousel(props: HeroBannerCarouselProps) {
  const {
    className,
    autoPlay = true,
    interval = INTERVAL_TIME,
    block: { slides, heroBannerTypeStyle, isActive },
  } = props;

  const options = useMemo(() => {
    const total = slides?.length || 0;
    return {
      slideCount: total,
      autoPlay: total > 1 ? autoPlay : false,
      interval: interval,
    };
  }, [slides, autoPlay, interval]);

  // Use the custom hook for all carousel logic
  const { emblaRef, selectedIndex, scrollNext, scrollPrev, scrollTo, scrollSnaps } = useHeroBannerSlide(options);

  const rawSize = (props.block as any)?.heroBannerTypeStyle as string | undefined;
  const effectiveSize = getHeroBannerSize(rawSize, heroBannerTypeStyle);
  const minHeightClass = getHeroBannerMinHeightClass(effectiveSize);

  // Early returns
  if (options.slideCount === 0 || !isActive) return null;
  if (!slides || slides.length === 0) return null;

  // Use Embla's scroll snaps once initialized (fallback to static total)
  const controlsTotal = scrollSnaps.length || options.slideCount;

  return (
    <div className="hero-banner-carousel-container relative w-full overflow-hidden">
      <div
        className={cn('relative h-auto', minHeightClass)}
        style={{
          touchAction: 'pan-y',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          minHeight: '400px',
        }}
      >
        <div className="embla relative h-full">
          <div
            className="embla__viewport h-full w-full overflow-hidden touch-pan-y"
            ref={emblaRef}
            style={{
              touchAction: 'pan-y',
              WebkitUserSelect: 'none',
              userSelect: 'none',
            }}
          >
            <div className="embla__container flex h-full">
              {slides.map((block, idx) => (
                <HeroBannerSlide
                  key={block._key || `slide-${idx}`}
                  block={block}
                  isActive={idx === selectedIndex}
                  shouldPreloadImage={idx === 0}
                  size={effectiveSize}
                  className={className}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {(effectiveSize === 'full' || effectiveSize === 'medium') && controlsTotal > 1 && (
        <HeroBannerControls
          total={controlsTotal}
          current={selectedIndex}
          onPrev={scrollPrev}
          onNext={scrollNext}
          onSlideChange={scrollTo}
          size={effectiveSize}
        />
      )}
    </div>
  );
}
